// SPDX-License-Identifier: AGPL-3.0-or-later

import Guilds from '@app/features/guild/state/Guilds';
import {GuildTagBadge} from '@app/features/guild_tag/components/GuildTagBadge';
import {GuildTagCard} from '@app/features/guild_tag/components/GuildTagCard';
import styles from '@app/features/guild_tag/components/GuildTagLabel.module.css';
import GuildTags from '@app/features/guild_tag/state/GuildTags';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import {usePopoutKeyContext} from '@app/features/ui/popover';
import {isPopoutDragClick, type PopoutClickStart} from '@app/features/ui/popover/PopoverDragClickUtils';
import {hasActiveTextSelectionInsideTarget, openPopout} from '@app/features/ui/popover/PopoverPopout';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef} from 'react';

interface GuildTagChipProps {
	tag: string;
	badge: number | null;
	size?: 'sm' | 'md';
	className?: string;
	title?: string;
	interactive?: Pick<
		React.HTMLAttributes<HTMLSpanElement>,
		'role' | 'tabIndex' | 'onClick' | 'onKeyDown' | 'onMouseDown'
	> & {
		'aria-haspopup': 'dialog';
	};
	'data-flx'?: string;
}

// Plain markup: it sits in message, member and voice rows, so no tooltip, timer or layout read.
export function GuildTagChip({
	tag,
	badge,
	size = 'sm',
	className,
	title,
	interactive,
	'data-flx': dataFlx,
}: GuildTagChipProps): React.ReactElement {
	return (
		<span
			className={clsx(styles.tag, size === 'md' && styles.md, interactive && styles.clickable, className)}
			title={title}
			{...interactive}
			data-flx={dataFlx ?? 'guild-tag.guild-tag-chip.tag'}
		>
			{badge !== null && (
				<GuildTagBadge badge={badge} className={styles.icon} data-flx="guild-tag.guild-tag-chip.icon" />
			)}
			<span data-flx="guild-tag.guild-tag-chip.text">{tag}</span>
		</span>
	);
}

interface GuildTagLabelProps {
	guildId: string;
	size?: 'sm' | 'md';
	className?: string;
	focusable?: boolean;
	'data-flx'?: string;
}

// Someone outside the guild sees the tag and nothing else: the guild name stays with its members. A row carries only
// these handlers; the card opens in the shared popout layer, and its first request goes out then.
export const GuildTagLabel = observer(function GuildTagLabel({
	guildId,
	size,
	className,
	focusable = false,
	'data-flx': dataFlx,
}: GuildTagLabelProps) {
	const parentPopout = usePopoutKeyContext();
	const clickStart = useRef<PopoutClickStart | null>(null);
	if (!GuildTags.enabled) return null;
	const view = GuildTags.tagFor(guildId);
	if (!view) return null;
	const openCard = (event: React.SyntheticEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		openPopout(
			event.currentTarget,
			{
				position: 'bottom-start',
				offsetMainAxis: 6,
				dependsOn: parentPopout ?? undefined,
				render: ({onClose}) => (
					<GuildTagCard
						guildId={guildId}
						tag={view.tag}
						chip={<GuildTagChip tag={view.tag} badge={view.badge} size="md" />}
						onClose={onClose}
					/>
				),
			},
			`guild-tag-card-${guildId}`,
		);
	};
	// A drag or a selection that ends on the tag is about the text around it, so the row keeps the click.
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		const start = clickStart.current;
		clickStart.current = null;
		if (event.shiftKey || isPopoutDragClick(start, event) || hasActiveTextSelectionInsideTarget(event.currentTarget)) {
			return;
		}
		openCard(event);
	};
	return (
		<GuildTagChip
			tag={view.tag}
			badge={view.badge}
			size={size}
			className={className}
			title={Guilds.getGuild(guildId)?.name}
			interactive={{
				role: 'button',
				tabIndex: focusable ? 0 : undefined,
				'aria-haspopup': 'dialog',
				onClick: handleClick,
				onMouseDown: (event) => {
					clickStart.current = {x: event.clientX, y: event.clientY, button: event.button};
				},
				onKeyDown: (event) => {
					if (isKeyboardActivationKey(event.key)) openCard(event);
				},
			}}
			data-flx={dataFlx}
		/>
	);
});
